import React, {useEffect, useMemo, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {css} from 'emotion';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import SiteDetailsTable from './SiteDetailsTable';
import {ConstraintValueRecord, DateIdMap} from '../utils/analyzeConstraints';
import {getConstraint} from '../../../common/apis/BackendApisClient';
import {useAppSelector} from '../../../app/hooks';
import {selectCachedBusiness, selectCachedCountry, selectCachedRegion} from '../../cached-state/cachedStateSlice';
import {getLocalizedAPIsStartDate, getLocalizedDateTimeStrings} from '../../../common/utils/dates';
import PageLoading from '../../../common/components/PageLoading';

const DAYS_PER_PAGE = 14;

interface IProps {
  history: RouteComponentProps['history'];
  pitConstraints: ConstraintValueRecord[];
  highlightedDates: DateIdMap;
  siteId: string;
}

const toggleRowStyle = css`
  padding-bottom: 8px;
`;

const SitePITConstraintTable = ({history, pitConstraints, highlightedDates, siteId}: IProps) => {
  const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
  const [perSlotDailyData, setPerSlotDailyData] = useState<ConstraintValueRecord[] | null>(null);

  // Expand weekly highlightedDates to cover all 7 days for the daily view
  const dailyHighlightedDates: DateIdMap = useMemo(() => {
    const result: DateIdMap = {};
    Object.keys(highlightedDates).forEach((weekKey) => {
      const [y, m, d] = weekKey.split('-').map(Number);
      const weekStart = new Date(y, m - 1, d);
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + i);
        const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(
          2,
          '0'
        )}`;
        result[dayKey] = highlightedDates[weekKey];
      }
    });
    return result;
  }, [highlightedDates]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [dayOffset, setDayOffset] = useState(0);
  const [totalDays, setTotalDays] = useState(0);

  const businessType = useAppSelector(selectCachedBusiness);
  const region = useAppSelector(selectCachedRegion);
  const country = useAppSelector(selectCachedCountry);

  const currDate = new Date();
  const startTime = getLocalizedAPIsStartDate(currDate);

  // Reset cached data when inputs change
  const constraintIds = pitConstraints.map((c) => c.constraintId).join(',');
  useEffect(() => {
    setPerSlotDailyData(null);
    setDayOffset(0);
  }, [constraintIds, businessType, region, country, siteId]);

  // Fetch per-slot data from the constraint endpoint when switching to day view
  useEffect(() => {
    if (viewMode === 'day' && !perSlotDailyData && businessType && region) {
      setLoadingDaily(true);

      const fetchPerSlotDaily = async () => {
        const rows: ConstraintValueRecord[] = [];
        let computedTotalDays = 91; // minimum fallback
        const pendingRows: {siteRow: any; dailyRecord: ConstraintValueRecord}[] = [];

        const results = await Promise.allSettled(
          pitConstraints.map((constraint) =>
            getConstraint({
              businessType: businessType!,
              region: region!,
              country: country ?? undefined,
              constraintId: Number(constraint.constraintId),
              startTime,
              query: siteId,
            })
          )
        );

        for (let i = 0; i < pitConstraints.length; i++) {
          const result = results[i];
          if (result.status === 'fulfilled' && result.value && result.value.valuesData && result.value.valuesData.length > 0) {
            const constraint = pitConstraints[i];
            const siteRow = result.value.valuesData.find((r) => r.siteId === siteId);
            if (siteRow) {
              // Determine total days dynamically from the date keys in the response
              const dateKeys = Object.keys(siteRow).filter((k) => k !== 'siteId' && k !== 'siteName');
              const maxDate = dateKeys.sort().pop();
              if (maxDate) {
                const [y, m, d] = maxDate.split('-').map(Number);
                const endDate = new Date(y, m - 1, d);
                const startDate = new Date(startTime);
                const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 7; // +7 for forward-fill buffer
                if (daysDiff > computedTotalDays) {
                  computedTotalDays = daysDiff;
                }
              }

              // Expand to daily with forward-fill
              const dailyRecord: ConstraintValueRecord = {
                constraintId: constraint.constraintId,
                constraintName: constraint.constraintName,
                constraintValueType: constraint.constraintValueType as string,
                constraintUsageTags: constraint.constraintUsageTags as string[],
                constraint: constraint.constraint,
              };

              pendingRows.push({siteRow, dailyRecord});
            }
          }
        }

        // Now expand all rows using the computed total days
        for (const {siteRow, dailyRecord} of pendingRows) {
          const startDate = new Date(startTime);
          let lastValue: string | undefined;

          for (let j = 0; j < computedTotalDays; j++) {
            const d = new Date(startDate);
            d.setDate(d.getDate() + j);
            const dayKey = getLocalizedDateTimeStrings(d).date;

            if (siteRow[dayKey] !== undefined && siteRow[dayKey] !== null && siteRow[dayKey] !== '') {
              lastValue = siteRow[dayKey];
            } else {
              const weekStart = new Date(d);
              weekStart.setDate(weekStart.getDate() - weekStart.getDay());
              const weekKey = getLocalizedDateTimeStrings(weekStart).date;
              if (siteRow[weekKey] !== undefined && siteRow[weekKey] !== null && siteRow[weekKey] !== '') {
                lastValue = siteRow[weekKey];
              }
            }

            if (lastValue !== undefined) {
              dailyRecord[dayKey] = lastValue;
            }
          }

          rows.push(dailyRecord);
        }

        setTotalDays(computedTotalDays);
        setPerSlotDailyData(rows);
        setLoadingDaily(false);
      };

      fetchPerSlotDaily();
    }
  }, [viewMode, perSlotDailyData, pitConstraints, businessType, region, country, siteId, startTime]);

  if (!pitConstraints || pitConstraints.length === 0) {
    return null;
  }

  // For daily view, slice to current page
  const getPagedDailyData = (): ConstraintValueRecord[] | null => {
    if (!perSlotDailyData) return null;

    const startDate = new Date(startTime);
    startDate.setDate(startDate.getDate() + dayOffset * 7);

    const pageDayKeys: string[] = [];
    for (let i = 0; i < DAYS_PER_PAGE; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      pageDayKeys.push(getLocalizedDateTimeStrings(d).date);
    }

    return perSlotDailyData.map((row) => {
      const pagedRow: ConstraintValueRecord = {
        constraintId: row.constraintId,
        constraintName: row.constraintName,
        constraintValueType: row.constraintValueType as string,
        constraintUsageTags: row.constraintUsageTags as string[],
        constraint: row.constraint,
      };
      pageDayKeys.forEach((key) => {
        if (row[key] !== undefined) {
          pagedRow[key] = row[key];
        }
      });
      return pagedRow;
    });
  };

  // Date range label for daily view
  const rangeStartDate = new Date(startTime);
  rangeStartDate.setDate(rangeStartDate.getDate() + dayOffset * 7);
  const rangeEndDate = new Date(rangeStartDate);
  rangeEndDate.setDate(rangeEndDate.getDate() + DAYS_PER_PAGE - 1);
  const rangeLabel = `${rangeStartDate.toLocaleString('default', {month: 'short', day: '2-digit'})} - ${rangeEndDate.toLocaleString(
    'default',
    {month: 'short', day: '2-digit'}
  )}`;

  if (viewMode === 'day' && loadingDaily) {
    return (
      <div>
        <Row className={toggleRowStyle} spacing="small">
          <Button
            type="tertiary"
            size="small"
            onClick={() => {
              setViewMode('week');
              setDayOffset(0);
            }}
          >
            Weekly Aggregate
          </Button>
          <Button type="primary" size="small">
            Per Slot
          </Button>
        </Row>
        <Row className={toggleRowStyle} spacing="small" alignmentVertical="center">
          <Button type="tertiary" size="small" disabled>
            ← Prev
          </Button>
          <Text type="b200">{rangeLabel}</Text>
          <Button type="tertiary" size="small" disabled>
            Next →
          </Button>
        </Row>
        <PageLoading />
      </div>
    );
  }

  const displayData = viewMode === 'week' ? pitConstraints : getPagedDailyData();

  if (!displayData) {
    return null;
  }

  return (
    <div>
      <Row className={toggleRowStyle} spacing="small">
        <Button
          type={viewMode === 'week' ? 'primary' : 'tertiary'}
          size="small"
          onClick={() => {
            setViewMode('week');
            setDayOffset(0);
          }}
        >
          Weekly Aggregate
        </Button>
        <Button type={viewMode === 'day' ? 'primary' : 'tertiary'} size="small" onClick={() => setViewMode('day')}>
          Per Slot
        </Button>
      </Row>
      {viewMode === 'day' && (
        <Row className={toggleRowStyle} spacing="small" alignmentVertical="center">
          <Button type="tertiary" size="small" onClick={() => setDayOffset(Math.max(0, dayOffset - 2))} disabled={dayOffset === 0}>
            ← Prev
          </Button>
          <Text type="b200">{rangeLabel}</Text>
          <Button
            type="tertiary"
            size="small"
            onClick={() => setDayOffset(dayOffset + 2)}
            disabled={(dayOffset + 2) * 7 + DAYS_PER_PAGE > totalDays}
          >
            Next →
          </Button>
        </Row>
      )}
      <SiteDetailsTable
        key={`${viewMode}-${dayOffset}`}
        history={history}
        allowSorting={false}
        allowPaging={false}
        allowFiltering={false}
        constraintData={displayData}
        highlightedDates={viewMode === 'day' ? dailyHighlightedDates : highlightedDates}
        summaryRecords={[]}
      />
    </div>
  );
};

export default SitePITConstraintTable;
